<template>
    <BaseCollapsible
        v-model:open="isOpen"
        stretched-trigger
        :trigger-aria-label="isOpen ? t('components.calendar.event.collapse', { title: event.title }) : t('components.calendar.event.expand', { title: event.title })"
        class="event-container group"
        :class="{ 'is-active': isActiveEvent, 'is-open': isOpen, 'is-tonal rounded-lgr': tonalThemeEnabled }"
        @update:open="handleFetchMeetingTips">
        <template #trigger>
            <div ref="calEvents" class="scroll-mt-10" :class="{ flex: !tonalThemeEnabled }">
                <div
                    v-if="!tonalThemeEnabled"
                    :data-event-id="event.event_id"
                    class="w-1.5 h-1.5 rounded-full mr-1 mt-1"
                    :class="isRightNow ? 'bg-accent' : 'bg-transparent'"></div>
                <CalEvent
                    class="w-full pointer-events-none"
                    :class="{ 'pb-1': !tonalThemeEnabled }"
                    :title="event.title"
                    :start-time="event.startTime"
                    :end-time="event.endTime"
                    :attendees="event.attendees ?? []"
                    :active="tonalThemeEnabled && (isActiveEvent || isOpen)"
                    :divider="tonalThemeEnabled && divider"
                    :highlighted="!tonalThemeEnabled && isOpen"
                    :calendar-color="calendarColor"
                    :is-past="isBeforeNow(event.endTime)"
                    :has-coaching-session="event.has_coaching_session">
                    <template #actions>
                        <template v-if="tonalThemeEnabled">
                            <BaseTooltip v-if="userDetails.is_valencer" :trigger-aria-label="t('components.calendar.event.addAttendeesTo', { title: event.title })">
                                <template #trigger>
                                    <BaseButton
                                        theme="neutral"
                                        size="sm"
                                        class="relative pointer-events-auto inline-flex items-center gap-2"
                                        :aria-label="t('components.calendar.event.addAttendeesTo', { title: event.title })"
                                        @click.stop="handleAddConnections">
                                        <LoadingSpinner v-if="isLoadingAddConnections" class="w-3 h-3" />
                                        <i v-else class="bi bi-plus-lg text-[7px]" aria-hidden="true"></i>
                                        <i class="bi bi-people-fill text-xs" aria-hidden="true"></i>
                                    </BaseButton>
                                </template>
                                <template #content>{{ t("components.calendar.event.addAttendees") }}</template>
                            </BaseTooltip>
                            <BaseButton
                                theme="primary"
                                size="sm"
                                class="relative pointer-events-auto"
                                :class="{ 'animate-shadow-pulse': isActiveEvent }"
                                :aria-label="t('components.calendar.event.ctaWithNadia', { cta: ctaButtonText, title: event.title })"
                                @click.stop="handleCalendarEventCta(event, isBeforeNow(event.startTime))">
                                {{ ctaButtonText }}
                            </BaseButton>
                            <i class="bi text-sm text-ink pointer-events-none" :class="isOpen ? 'bi-chevron-up' : 'bi-chevron-down'" aria-hidden="true"></i>
                        </template>
                        <div v-else class="relative pointer-events-auto transition-opacity whitespace-nowrap" :class="ctaOpacityClass">
                            <BaseTooltip v-if="userDetails.is_valencer" :trigger-aria-label="t('components.calendar.event.addAttendeesTo', { title: event.title })">
                                <template #trigger>
                                    <BaseButton
                                        theme="neutral"
                                        size="sm"
                                        class="relative pointer-events-auto inline-flex items-center gap-2 mr-2"
                                        :aria-label="t('components.calendar.event.addAttendeesTo', { title: event.title })"
                                        @click.stop="handleAddConnections">
                                        <LoadingSpinner v-if="isLoadingAddConnections" class="inline w-3 h-3 mb-1" />
                                        <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -- punctuation: decorative plus sign, hidden from assistive tech -->
                                        <template v-else><span aria-hidden="true">+</span> <i class="bi bi-people-fill" aria-hidden="true"></i></template>
                                    </BaseButton>
                                </template>
                                <template #content>{{ t("components.calendar.event.addAttendees") }}</template>
                            </BaseTooltip>
                            <BaseButton
                                class="relative pointer-events-auto whitespace-nowrap px-6 py-1 rounded-3xl body-small font-medium mr-2 text-ink cta-button"
                                :class="{ 'animate-shadow-pulse': isActiveEvent }"
                                theme="primary"
                                :aria-label="t('components.calendar.event.ctaWithNadia', { cta: ctaButtonText, title: event.title })"
                                @click.stop="handleCalendarEventCta(event, isBeforeNow(event.startTime))">
                                {{ ctaButtonText }}
                            </BaseButton>
                            <i class="bi pr-3 pointer-events-none" :class="isOpen ? 'bi-chevron-up' : 'bi-chevron-down'" aria-hidden="true"></i>
                        </div>
                    </template>
                </CalEvent>
            </div>
        </template>
        <template #content>
            <div class="grid md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-4 grow" :class="tonalThemeEnabled ? 'my-2' : 'ml-2'">
                <component :is="tipsContainer" v-bind="tipsContainerProps" :class="{ 'px-4 py-5 rounded-lg bg-surface-raised border border-border-strong': !tonalThemeEnabled }">
                    <div class="border-b border-border text-ink-muted body-small pb-4 mb-4">
                        <i class="bi bi-lightbulb mr-2" aria-hidden="true"></i>{{ t("components.calendar.event.tipsTitle") }}
                    </div>
                    <div class="body-small overflow-auto max-h-50 text-ink-muted">
                        <template v-if="isFetching">
                            <SkeletonLoader v-for="n in 4" :key="n" class="w-full h-[0.75lh] rounded-md my-1" />
                            <SkeletonLoader class="w-3/5 h-[0.75lh] rounded-md my-1" />
                        </template>
                        <Transition v-else name="fade" mode="out-in">
                            <span v-if="meetingTip">
                                {{ meetingTip }}
                            </span>
                            <span v-else-if="error" role="alert">{{ t("components.calendar.event.tipFailed") }}</span>
                            <span v-else>{{ t("components.calendar.event.noTips") }}</span>
                        </Transition>
                    </div>
                </component>
                <CalendarAttendees :attendees="event.attendees" />
            </div>
        </template>
    </BaseCollapsible>
</template>

<script setup lang="ts">
import { isBeforeNow, isBetween } from "/js/dateUtils";
import { useIntervalFn } from "@vueuse/core";
import { useFetch } from "~vue/api";
import type { UserDetails } from "~vue/appTypes";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { useCalendarStore } from "~vue/stores/calendarStore";
import type { CalendarIntegrationEventInput, ProcessedCalendarEvent } from "~vue/types/calendar_integration";
import { openUrl } from "~vue/utils";
import { CHATSOURCE } from "~vue/utils/constants";
import { logUserInteraction } from "~vue/utils/logUtils";
import { isSessionEndedError } from "~vue/utils/sessionEnded";
import { tonalThemeEnabled } from "~vue/utils/theme";
import { computed, type ComputedRef, inject, onMounted, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import BaseButton from "../BaseButton.vue";
import BaseCard from "../BaseCard.vue";
import BaseCollapsible from "../BaseCollapsible.vue";
import BaseTooltip from "../BaseTooltip.vue";
import SkeletonLoader from "../SkeletonLoader.vue";
import CalendarAttendees from "./CalendarAttendees.vue";
import { CTA_OPACITY_ACTIVE, CTA_OPACITY_DEFAULT, CTA_OPACITY_OPEN } from "./calendarConstants";
import CalEvent from "./CalEvent.vue";

const { t } = useI18n();

type Props = {
    event: ProcessedCalendarEvent;
    divider?: boolean;
    calendarColor?: string;
    buttonColor?: string;
};

const { event, divider = false, calendarColor = "#9596ff", buttonColor } = defineProps<Props>();
// TODO: Remove the legacy calendar branch once the tonal theme becomes the default.
const tipsContainer = computed(() => (tonalThemeEnabled.value ? BaseCard : "div"));
const tipsContainerProps = computed(() => (tonalThemeEnabled.value ? { padding: "sm" as const } : {}));

const userDetails = inject<ComputedRef<UserDetails>>("user_details")!;

const ctaButtonText = computed(() => {
    if (event.has_coaching_session) {
        return t("components.calendar.event.revisit");
    }
    return isBeforeNow(event.startTime) ? t("components.calendar.event.debrief") : t("components.calendar.event.prepare");
});

const handleCalendarEventCta = (calendarEvent: ProcessedCalendarEvent, isBefore: boolean) => {
    if (isBefore) {
        logUserInteraction(calendarEvent.has_coaching_session ? "calendar_event_revisit" : "calendar_event_debrief");
        openUrl("/coach/start/", {
            queryParams: {
                calendar_integration_event_data: JSON.stringify({ event_id: calendarEvent.event_id, convo_type: "debrief" }),
                chat_source: CHATSOURCE.CALENDAR_DEBRIEF,
            },
        });
    } else {
        logUserInteraction(calendarEvent.has_coaching_session ? "calendar_event_revisit" : "calendar_event_prep");
        openUrl("/coach/start/", {
            queryParams: {
                calendar_integration_event_data: JSON.stringify({ event_id: calendarEvent.event_id, convo_type: "prep" }),
                chat_source: CHATSOURCE.CALENDAR_PREPARE,
            },
        });
    }
};

const attendeeMap = computed(() => {
    return (event.attendees ?? []).map((attendee) => ({
        name: attendee.name,
        email: attendee.email,
    }));
});

const handleAddConnections = async () => {
    logUserInteraction("calendar_add_connection_clicked");
    await addConnections();
    if (addedConnections.value) {
        if (addedConnections.value.connections.length === 0) {
            logUserInteraction("calendar_no_new_connection");
        }
        useCalendarStore.setDialogAttendees(addedConnections.value.connections);
        useCalendarStore.setShowAttendeesDialog(true);
    }
};

const meetingTipsFetchUrl = computed(() => `/api/calendar_integration/meeting_tip/${event.event_id}/`);
const {
    execute: fetchMeetingTips,
    data: tips,
    isFetching,
    error,
    onFetchResponse,
    onFetchError: onFetchMeetingTipError,
} = useFetch(meetingTipsFetchUrl, { immediate: false }).post().json();
onFetchMeetingTipError(() => {
    logUserInteraction("calendar_tip_errored");
});
onFetchResponse(() => {
    logUserInteraction("calendar_tip_generated");
});

const meetingTip = computed(() => event.meeting_tip ?? tips.value?.meeting_tip);

const handleFetchMeetingTips = (open: boolean) => {
    if (open && !meetingTip.value) {
        logUserInteraction("calendar_event_expanded");
        fetchMeetingTips();
    }
};

const isOpen = ref(false);
const isActiveEvent = computed(() => useCalendarStore.activeEvent?.event_id === event.event_id);

const ctaOpacityClass = computed(() => {
    if (isOpen.value) {
        return CTA_OPACITY_OPEN;
    }
    if (isActiveEvent.value) {
        return CTA_OPACITY_ACTIVE;
    }
    return CTA_OPACITY_DEFAULT;
});

const buttonColorToken = computed(() => buttonColor ?? calendarColor);
const hoverButtonColor = computed(() => `${buttonColorToken.value}CC`);

const isRightNow = ref(!tonalThemeEnabled.value && isBetween(event.startTime, event.endTime));
if (!tonalThemeEnabled.value) {
    const { pause } = useIntervalFn(() => {
        isRightNow.value = isBetween(event.startTime, event.endTime);
        if (isBeforeNow(event.endTime)) {
            pause();
        }
    }, 1000);
}

const bulkCreatePayload = computed(() => {
    const backendEvent: CalendarIntegrationEventInput = {
        provider: event.provider,
        event_id: event.event_id,
        subject: event.title,
        description: event.description,
        attendees: event.attendees ?? [],
        start_time: event.startTime.toISO(),
        end_time: event.endTime.toISO(),
    };

    return { events: [backendEvent], people: attendeeMap.value };
});
const {
    execute: addConnections,
    isFetching: isLoadingAddConnections,
    data: addedConnections,
    error: addConnectionsError,
    onFetchError: onFetchAddConnectionsError,
} = useFetch("/api/connections/bulk/", { immediate: false }).post(bulkCreatePayload).json();

const emit = defineEmits<{
    error: [message: string];
}>();

onFetchAddConnectionsError(() => {
    const genericError = t("components.calendar.event.addPeopleFailed");
    logUserInteraction("calendar_add_connection_error");
    if (!isSessionEndedError(addConnectionsError.value)) {
        emit("error", genericError);
    }
});

const calEvents = useTemplateRef("calEvents");

onMounted(() => {
    if (isActiveEvent.value) {
        useCalendarStore.setActiveElement(calEvents.value);
    }
});
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

.event-container.is-tonal:hover :deep(.event),
.event-container.is-tonal:has(:focus-visible) :deep(.event) {
    @apply bg-surface;
}

.event-container.is-tonal:hover :deep(.event-title),
.event-container.is-tonal:has(:focus-visible) :deep(.event-title) {
    text-shadow: currentcolor 0.3px 0 0;
}

.event-container.is-tonal:hover :deep(.actions),
.event-container.is-tonal:has(:focus-visible) :deep(.actions) {
    @apply opacity-100 pointer-events-auto;
}

.event-container.is-tonal:hover :deep(.event)::after,
.event-container.is-tonal:has(:focus-visible) :deep(.event)::after {
    @apply hidden;
}

@layer components {
    .event-container:not(.is-tonal):hover :deep(.legacy-event),
    .event-container:not(.is-tonal):has(:focus-visible) :deep(.legacy-event) {
        @apply bg-surface-hover;
    }
}

.cta-button {
    background-color: v-bind(buttonColorToken);

    &:hover {
        background-color: v-bind(hoverButtonColor);
    }
}
</style>
